// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React from 'react';
import { Route, Switch } from 'wouter';
import { AuthProvider } from './context/AuthContext';
import { I18nProvider } from './i18n';
import ProtectedRoute from './components/ProtectedRoute';
import { administreUneLicence, peutVerifierCertification } from './lib/acces';
import AppLayout from './components/AppLayout';
import Dashboard from './pages/Dashboard';
import Analyses from './pages/Analyses';
import AnalysisView from './pages/AnalysisView';
import AdminAnnotations from './pages/AdminAnnotations';
import AdminStats from './pages/AdminStats';
import AdminAuditLogs from './pages/AdminAuditLogs';
import MesStatistiques from './pages/MesStatistiques';
import Login from './pages/Login';
import Signup from './pages/Signup';
import InviteAccept from './pages/InviteAccept';
import { ForgotPassword, ResetPassword } from './pages/PasswordReset';
import AdminRatings from './pages/AdminRatings';
import AdminRetoursAvis from './pages/AdminRetoursAvis';
import AdminUsers from './pages/AdminUsers';
import AdminEnterpriseClients from './pages/AdminEnterpriseClients';
import LicensePortal from './pages/LicensePortal';
import Projects from './pages/Projects';
import LegalPage from './pages/LegalPage';
import Settings from './pages/Settings';
import Nouveautes from './pages/Nouveautes';
import HelpPage from './pages/HelpPage';
import VerifierCertification from './pages/VerifierCertification';

const WithLayout = ({ children }) => (
  <ProtectedRoute>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <Switch>
          <Route path="/login" component={Login} />
          <Route path="/signup" component={Signup} />
          <Route path="/invite/:token" component={InviteAccept} />
          {/* Mot de passe oublié — publiques : l'utilisateur ne peut, par
              définition, pas être authentifié pour y accéder. */}
          <Route path="/forgot-password" component={ForgotPassword} />
          <Route path="/reset-password/:token" component={ResetPassword} />

          {/* Pages légales — publiques, sans authentification */}
          <Route path="/privacy"><LegalPage type="privacy" /></Route>
          <Route path="/cgv"><LegalPage type="cgv" /></Route>
          <Route path="/mentions-legales"><LegalPage type="mentions" /></Route>
          <Route path="/avertissement-ia"><LegalPage type="avertissement-ia" /></Route>
          <Route path="/sla"><LegalPage type="sla" /></Route>

          {/* Aide — PUBLIQUE, et c'est le point : les deux motifs de
              consultation les plus fréquents (« je n'arrive plus à me
              connecter », « double authentification ») surviennent quand
              l'utilisateur est DEHORS. Derrière l'authentification, la page
              les raterait tous les deux. */}
          <Route path="/aide"><HelpPage /></Route>

          <Route path="/dashboard">
            <WithLayout><Dashboard /></WithLayout>
          </Route>

          <Route path="/analyses">
            <WithLayout><Analyses /></WithLayout>
          </Route>

          <Route path="/projets">
            <WithLayout><Projects /></WithLayout>
          </Route>

          <Route path="/parametres">
            <WithLayout><Settings /></WithLayout>
          </Route>

          <Route path="/mes-statistiques">
            <WithLayout><MesStatistiques /></WithLayout>
          </Route>

          {/* Chantier ⑩ : à partir de Pro. Même règle que l'entrée de menu
              (`lib/acces.js`) ; la serrure est la route serveur (403). */}
          <Route path="/verifier-certification">
            <ProtectedRoute autorise={peutVerifierCertification}>
              <AppLayout><VerifierCertification /></AppLayout>
            </ProtectedRoute>
          </Route>

          <Route path="/nouveautes">
            <WithLayout><Nouveautes /></WithLayout>
          </Route>

          <Route path="/analysis/:id">
            {() => <WithLayout><AnalysisView /></WithLayout>}
          </Route>

          <Route path="/admin/annotations">
            <ProtectedRoute roles={['admin', 'dev']}>
              <AppLayout><AdminAnnotations /></AppLayout>
            </ProtectedRoute>
          </Route>
          <Route path="/admin/ratings">
            <ProtectedRoute roles={['admin', 'dev']}>
              <AppLayout><AdminRatings /></AppLayout>
            </ProtectedRoute>
          </Route>
          {/* Tri des signalements « cet avis est faux » (S3, 25/08/2026).
              Réservé à Evalens : l'écran croise les signalements de TOUS les
              comptes et porte leurs adresses. */}
          <Route path="/admin/retours">
            <ProtectedRoute roles={['admin', 'dev']}>
              <AppLayout><AdminRetoursAvis /></AppLayout>
            </ProtectedRoute>
          </Route>
          <Route path="/admin/stats">
            <ProtectedRoute roles={['admin', 'dev']}>
              <AppLayout><AdminStats /></AppLayout>
            </ProtectedRoute>
          </Route>
          {/* Journal d'audit (5/08/2026) — la table avait 60+ points d'écriture
              et aucune lecture. Réservé à Evalens : il porte les emails et les
              adresses IP de tous les comptes, y compris ceux des clients. */}
          <Route path="/admin/audit">
            <ProtectedRoute roles={['admin', 'dev']}>
              <AppLayout><AdminAuditLogs /></AppLayout>
            </ProtectedRoute>
          </Route>

          <Route path="/">
            <WithLayout><Dashboard /></WithLayout>
          </Route>
          <Route path="/admin/users">
            <ProtectedRoute roles={['admin', 'dev']}>
              <AppLayout><AdminUsers /></AppLayout>
            </ProtectedRoute>
          </Route>
          <Route path="/admin/enterprise">
            <ProtectedRoute roles={['admin', 'dev']}>
              <AppLayout><AdminEnterpriseClients /></AppLayout>
            </ProtectedRoute>
          </Route>
          {/* ⚠️ La SEULE page sensible qui n'était pas gardée (22/09/2026) :
              tout compte connecté l'ouvrait, et le bouton « page précédente »
              du navigateur la restituait telle quelle après un changement de
              compte — écran d'administration à l'image du compte précédent.
              Les routes serveur refusaient bien (403), mais l'écran restait. */}
          <Route path="/license-portal">
            <ProtectedRoute autorise={administreUneLicence}>
              <AppLayout><LicensePortal /></AppLayout>
            </ProtectedRoute>
          </Route>
          <Route>
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-6xl font-bold text-gray-800 mb-4">404</h1>
                <a href="/dashboard" className="text-blue-600 hover:underline">Retour au dashboard</a>
              </div>
            </div>
          </Route>
        </Switch>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;